/**
 * Laravel Echo Configuration for Web Application
 *
 * Initializes Echo configuration from Vite environment variables
 */

import {
  createEchoConfig,
  type EchoConfig,
} from '@personal-trainer-app/shared';
import { getEnvSafe } from '@personal-trainer-app/shared/src/utils/env';

function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/**
 * Convert an auth endpoint into an absolute URL that points at the Laravel API.
 *
 * - If `VITE_REVERB_AUTH_ENDPOINT` is absolute, use it as-is.
 * - If it's a path (e.g. `/api/broadcasting/auth`), prefix with API origin.
 * - If it's missing, default to `${VITE_API_BASE_URL}/broadcasting/auth`
 *   (so `http://localhost:8000/api/broadcasting/auth` for this app's typical setup).
 */
function resolveBroadcastAuthEndpoint(
  apiBaseUrl: string,
  envAuthEndpoint?: string
): string {
  const trimmedApiBase = apiBaseUrl.replace(/\/+$/, '');

  if (!envAuthEndpoint) {
    return `${trimmedApiBase}/broadcasting/auth`;
  }

  const raw = envAuthEndpoint.trim();
  if (!raw) {
    return `${trimmedApiBase}/broadcasting/auth`;
  }

  if (isAbsoluteUrl(raw)) {
    return raw;
  }

  // Treat as a path; prefix with API origin (not /api), e.g.:
  // apiBaseUrl=http://localhost:8000/api + /broadcasting/auth -> http://localhost:8000/broadcasting/auth
  // apiBaseUrl=http://localhost:8000/api + /api/broadcasting/auth -> http://localhost:8000/api/broadcasting/auth
  const origin = new URL(trimmedApiBase).origin;
  return raw.startsWith('/') ? `${origin}${raw}` : `${origin}/${raw}`;
}

/**
 * Get Echo configuration from environment variables
 *
 * Environment variables used:
 * - VITE_REVERB_HOST: Reverb server host (default: 'localhost')
 * - VITE_REVERB_PORT: Reverb server port (default: 8080)
 * - VITE_REVERB_APP_KEY: Application key from Laravel (default: 'app-key')
 * - VITE_REVERB_SCHEME: Connection scheme - 'http' or 'https' (default: 'http')
 * - VITE_REVERB_AUTH_ENDPOINT: Auth endpoint for private channels (default: '/broadcasting/auth')
 *
 * @returns EchoConfig object ready for use with `@laravel/echo-react` / `EchoProvider`
 *
 * @example
 * ```typescript
 * import { getEchoConfig } from '@/config';
 *
 * // Typically used indirectly via `EchoProvider` which calls `configureEcho(...)`
 * // with this config (plus auth headers) once a user is authenticated.
 * const config = getEchoConfig();
 * ```
 */
export function getEchoConfig(): EchoConfig {
  const apiBaseUrl = getEnvSafe(
    'VITE_API_BASE_URL',
    'http://localhost:8000/api'
  );
  const envAuthEndpoint = getEnvSafe('VITE_REVERB_AUTH_ENDPOINT', '');
  const trimmedAuthEndpoint = envAuthEndpoint.trim();

  const config = createEchoConfig({
    host: getEnvSafe('VITE_REVERB_HOST', ''),
    port: getEnvSafe('VITE_REVERB_PORT', ''),
    key: getEnvSafe('VITE_REVERB_APP_KEY', ''),
    scheme: getEnvSafe('VITE_REVERB_SCHEME', ''),
    // App default: auth route lives under /api.
    // (Can be overridden via VITE_REVERB_AUTH_ENDPOINT.)
    authEndpoint: trimmedAuthEndpoint || '/api/broadcasting/auth',
  });

  // Ensure broadcasting auth requests go to Laravel API, not the Vite dev server.
  config.authEndpoint = resolveBroadcastAuthEndpoint(
    apiBaseUrl,
    config.authEndpoint
  );

  return config;
}

/**
 * Echo configuration instance
 * Use this for a singleton config across your application
 */
export const echoConfig = getEchoConfig();
